import React from 'react';
import { GraduationCap, BookOpen, Building, Library, Users, Briefcase } from 'lucide-react';
import { environmentImages } from '../data/mockData';

const UseCases = () => {
  const useCases = [
    {
      icon: GraduationCap,
      title: 'Colleges & Universities',
      description: 'Perfect for students printing assignments, notes, and project reports on-the-go',
      image: environmentImages.students,
      stats: '5K+ students daily'
    },
    {
      icon: BookOpen,
      title: 'Schools',
      description: 'Easy access for students and teachers to print educational materials instantly',
      image: environmentImages.library,
      stats: 'K-12 & Higher Ed'
    },
    {
      icon: Building,
      title: 'Government Offices',
      description: 'Efficient document printing for public services and official work',
      image: environmentImages.office,
      stats: 'Secure & Compliant'
    },
    {
      icon: Library,
      title: 'Libraries',
      description: 'Self-service printing for library members and researchers 24/7',
      image: environmentImages.library,
      stats: 'Public & Academic'
    },
    {
      icon: Users,
      title: 'Co-working Spaces',
      description: 'On-demand printing for freelancers and remote workers',
      image: environmentImages.office,
      stats: 'Pay-per-use'
    },
    {
      icon: Briefcase,
      title: 'Corporate Offices',
      description: 'Quick document printing for meetings and presentations',
      image: environmentImages.office,
      stats: 'Enterprise Ready'
    }
  ];

  return (
    <section id="use-cases" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-yellow-500 font-semibold text-sm uppercase tracking-wide">Use Cases</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-3 mb-6">
            Trusted Across Industries
          </h2>
          <p className="text-lg text-gray-600">
            From educational institutions to corporate offices, PRINTLEY is transforming printing 
            experiences everywhere.
          </p>
        </div>

        {/* Use Cases Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => (
            <div 
              key={index}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              {/* Image with Overlay */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={useCase.image} 
                  alt={useCase.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent"></div>
                
                {/* Stats Badge */}
                <div className="absolute top-4 right-4 bg-yellow-500 text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                  {useCase.stats}
                </div>

                {/* Icon */}
                <div className="absolute bottom-4 left-4">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <useCase.icon className="w-7 h-7 text-yellow-500" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-yellow-600 transition-colors duration-200">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {useCase.description}
                </p>
              </div>

              {/* Hover Border Effect */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-yellow-200 rounded-2xl transition-colors duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-16 bg-white rounded-3xl p-12 shadow-lg border-2 border-gray-100">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Bring PRINTLEY to Your Location?
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              Join hundreds of institutions and businesses already using PRINTLEY to provide 
              seamless printing services to their communities.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-8 py-4 rounded-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
              >
                Partner with Us
              </button>
              <button 
                onClick={() => document.querySelector('#faq')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold px-8 py-4 rounded-lg transition-all duration-200"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
